<template>
  <div class="multiple-choice-answers" :class="{ locked: lockAnswer }">
    <ul
      class="multiple-choice-answer-list"
      :class="{
        'long-answers': longAnswers,
        'is-validated': isValidated,
        'show-focus': enableShowFocus,
      }"
      role="radiogroup"
      :aria-labelledby="labelledBy"
    >
      <li
        v-for="answer in answers"
        :key="`${questionId}-${answer.id}`"
        class="multiple-choice-answer"
        :class="validationClassName(answer)"
      >
        <input
          type="radio"
          class="multiple-choice-radio-input"
          :id="`multiple-choice-answer-${questionId}-${answer.id}`"
          :name="inputName"
          :value="answer.id"
          v-model="userAnswer"
          :disabled="lockAnswer"
          :ref="'multipleChoiceRadioInput' + answer.id"
        />
        <label
          class="multiple-choice-answer-label"
          :for="`multiple-choice-answer-${questionId}-${answer.id}`"
          v-html="answer.answer"
          @click="clicked($event, answer.id)"
        ></label>
        <FaIcon
          icon="circle"
          class="circle-icon circle-icon-hover"
          type="solid"
          :hidden="displayValidationIcon(answer)"
        />
        <FaIcon
          icon="circle"
          class="circle-icon"
          type="regular"
          :hidden="displayValidationIcon(answer)"
        />
        <FaIcon
          v-if="displayValidationIcon(answer)"
          :icon="answer.correct ? 'check' : 'times'"
          class="validation-icon"
          :class="answer.correct ? 'validation-icon-correct' : ''"
        />
        <TextToSpeech :text="answer.answer" />
      </li>
    </ul>
  </div>
</template>

<script>
import { removeTags } from '../../utils/string'
import { KeyCodes } from '../../utils/keycodes'

import BaseAnswer from './BaseAnswer'
import TextToSpeech from '../accessibility/TextToSpeech'
import FaIcon from '../ui/FaIcon'

export default {
  components: { FaIcon, TextToSpeech },
  extends: BaseAnswer,
  data() {
    try {
      return {
        radioInputs: [],
        // Preset the user answer if it exists
        userAnswer: this.data.UserAnswerMultipleChoice
          .question_answer_multiple_choice_id,
        enableShowFocus: false,
      }
    } catch (err) {
      // Don't set any data if (userAnswer is already defined in BaseAnswer.vue)
      return {
        radioInputs: [],
        enableShowFocus: false,
      }
    }
  },
  mounted() {
    this.radioInputs = document.getElementsByName(this.inputName)

    window.addEventListener('keydown', this.onKeyDown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
  },
  computed: {
    inputName() {
      return `multiple-choice-answer-${this.questionId}`
    },
    canValidate() {
      return typeof this.answers[0].correct === 'boolean'
    },
    isValidated() {
      return (
        this.canValidate &&
        this.userAnswer !== undefined &&
        this.userAnswer !== null
      )
    },
    answers() {
      return this.data.answers.map(answer => {
        return answer.Answer
      })
    },
    longAnswers() {
      return this.answers.some(answer => {
        return removeTags(answer.answer).length > 10
      })
    },
  },
  methods: {
    focus() {
      this.$el.querySelector('input').focus()
    },
    blur() {
      this.$el.querySelector('input').blur()
    },
    displayValidationIcon(answer) {
      return (
        this.canValidate && (answer.id === this.userAnswer || answer.correct)
      )
    },
    clicked(event, answerId) {
      if (this.isValidated) {
        return
      }

      this.enableShowFocus = true

      let clickedRadioInput = this.$refs[
        'multipleChoiceRadioInput' + answerId
      ][0]

      if (clickedRadioInput.checked) {
        event.preventDefault()
        event.stopPropagation()

        this.clearSelection()
      }
    },
    onKeyDown(event) {
      if (!this.isActive || this.disableKeyboard) {
        return
      }

      if (this.enableShowFocus) {
        switch (event.key) {
          case KeyCodes.SPACE:
          case KeyCodes.ENTER:
            if (!this.userAnswer) {
              let focusedRadioInput = this.$el.querySelector(
                '.multiple-choice-radio-input:focus'
              )

              if (focusedRadioInput) {
                focusedRadioInput.click()
              }
            }
        }
      } else {
        let cancelKey = false

        switch (event.key) {
          case KeyCodes.ARROW_UP:
          /* falls through */
          case KeyCodes.ARROW_DOWN:
            if (!this.userAnswer) {
              this.radioInputs[0].click()
              cancelKey = true
            }

          /* falls through */
          case KeyCodes.TAB:
            this.enableShowFocus = true
            if (!this.userAnswer || cancelKey) {
              event.preventDefault()
              return
            }

            break
        }
      }

      if (this.canValidate || event.key !== KeyCodes.ESCAPE) {
        return
      }

      event.preventDefault()
      this.clearSelection()
    },
    /**
     * Clears any answer selection made by the user.
     */
    clearSelection() {
      this.radioInputs.forEach(function(radioInput) {
        radioInput.checked = false
      })

      this.userAnswer = null
    },
    validationClassName(answer) {
      if (this.userAnswer === answer.id && this.canValidate) {
        return answer.correct ? 'correct' : 'wrong'
      }
    },
  },
  watch: {
    isActive(isActive) {
      if (!isActive) {
        this.enableShowFocus = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
$answerSeparatorColor: #dadada;

.multiple-choice-answers {
  border-top: 1px solid $answerSeparatorColor;
  margin-top: 1em;
  padding-top: 0.5em;
}

.multiple-choice-answer-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  clip-path: inset(1px 2px 1px);
  transition: color 0.2s ease-in-out;

  // Override default list style
  list-style: none;
  padding: 0;

  &.long-answers {
    grid-template-columns: repeat(1, 1fr);
  }

  &.is-validated {
    color: mix($textColor, transparent);
  }

  @media (max-width: 23rem) {
    grid-template-columns: repeat(1, 1fr);
  }
}

.multiple-choice-answer {
  position: relative;

  // Overwrite the default <li> style from Fessor
  padding-left: 0;
  padding-bottom: 0;
  background-image: none;

  .circle-icon,
  .validation-icon,
  .text-to-speech {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .circle-icon {
    left: 1.25em;
    border: 0 solid transparent;
    border-radius: 100%;
    transition: all 0.2s ease-out;
    pointer-events: none;
  }

  .circle-icon-hover {
    opacity: 0;
  }

  .validation-icon {
    left: 0.67em;
    font-size: 1.5em;
  }

  .validation-icon-correct {
    color: $successText;
  }

  .text-to-speech {
    right: 1.25em;
  }

  &.correct {
    color: $successText;
  }

  &.wrong {
    color: $errorText;
  }
}

.multiple-choice-answer-label {
  display: block;
  background: white;
  height: 100%;
  border: 1px solid $answerSeparatorColor;
  margin: 0 -1px -1px 0;
  padding: 0 2.5em;
  text-align: left;
  font-weight: $fontWeightMedium;
  font-size: 1.25em;
  align-items: center;

  > p {
    margin: 0.5em 0;
  }

  // Sneaky (and ugly) trick to target MS Edge only
  @supports (-ms-ime-align: auto) {
    // Edge sometimes has a bug where height 100% expands the parent element to take up the
    // entire height of its parent element making the multiple choice answers HUUUUGE
    height: auto;
  }
}

.multiple-choice-answers:not(.locked) {
  .multiple-choice-answer-label {
    cursor: pointer;

    @media (hover: hover) {
      &:hover ~ .circle-icon-hover {
        opacity: 0.5;
      }
    }
  }
}

.multiple-choice-radio-input {
  position: absolute;
  opacity: 0;

  &:checked ~ .circle-icon-hover {
    transition-duration: 0s;
    opacity: 1;
  }
}

.show-focus .multiple-choice-radio-input {
  &:focus ~ .circle-icon:not(.circle-icon-hover) {
    border-width: 0.5em;
    border-color: $answerSeparatorColor;
    transform: translate(-0.5em, -50%);
  }
}

@media (max-width: 25em) {
  .multiple-choice-answer {
    .circle-icon {
      left: 0.75em;
    }

    .validation-icon {
      left: 0.5em;
    }
    .validation-icon-correct {
      left: 0.35em;
    }

    .text-to-speech {
      right: 0.75em;
    }
  }
  .multiple-choice-answer-label {
    padding: 0 1.75em;
  }
}
</style>
